/**
 * API module for browser -> backend and server -> backend requests.
 *
 * Client-side: uses VITE_API_URL (public internet).
 * Server-side (loaders, server fns): uses API_URL if set (e.g. Railway internal
 * URL like http://backend.railway.internal:3001), falling back to VITE_API_URL.
 *
 * All browser requests use credentials: 'include' for HTTP-only cookies.
 */

/** Client-side backend URL (baked into bundle). */
export const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

/**
 * Server-side backend URL for loaders and server functions.
 * Uses API_URL env var (runtime, not baked) when available for internal routing.
 * Falls back to the client URL.
 */
const SERVER_API_URL =
  typeof globalThis.process !== 'undefined'
    ? (globalThis.process.env?.API_URL || API_URL)
    : API_URL

/**
 * Absolute backend URL for full-page navigations (OAuth redirects, file downloads).
 * Always uses the public URL since these are browser-initiated.
 */
export const BACKEND_URL = API_URL

function getBaseUrl(): string {
  return typeof window === 'undefined' ? SERVER_API_URL : API_URL
}

export async function requestWithCredentials(
  endpoint: string,
  options?: RequestInit,
): Promise<Response> {
  const baseUrl = getBaseUrl()
  const url = `${baseUrl}${endpoint}`

  const defaultHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  const isFormData = typeof FormData !== 'undefined' && options?.body instanceof FormData
  const headers: Record<string, string> = {
    ...(isFormData ? {} : defaultHeaders),
    ...(options?.headers as Record<string, string> | undefined),
  }

  const response = await doFetch(url, { ...options, headers, credentials: 'include' }, baseUrl)

  // On 401, attempt a single token refresh and retry the original request
  if (response.status === 401 && typeof window !== 'undefined') {
    const refreshed = await refreshSession()
    if (refreshed) {
      return doFetch(url, { ...options, headers, credentials: 'include' }, baseUrl)
    }
  }

  return response
}

async function doFetch(url: string, init: RequestInit, baseUrl: string): Promise<Response> {
  try {
    return await fetch(url, init)
  } catch (fetchError) {
    const errorMessage = fetchError instanceof Error ? fetchError.message : 'Network error'
    if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
      throw new Error(
        `Unable to connect to the server. Please ensure the backend is running at ${baseUrl}. ` +
          `If you're running in development, check that the backend server is started.`,
      )
    }
    throw new Error(`Network request failed: ${errorMessage}`)
  }
}

// ---------------------------------------------------------------------------
// Session refresh with concurrency guard
// ---------------------------------------------------------------------------

let refreshPromise: Promise<boolean> | null = null

async function refreshSession(): Promise<boolean> {
  // If a refresh is already in flight, wait for it instead of firing another
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    try {
      const baseUrl = typeof window === 'undefined' ? API_URL : API_URL
      const res = await fetch(`${baseUrl}/api/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      })
      return res.ok
    } catch {
      return false
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

export async function getApiErrorMessage(
  response: Response,
  fallbackMessage: string,
): Promise<string> {
  try {
    const error = await response.json()
    return error.error?.message || error.message || fallbackMessage
  } catch {
    return response.statusText || fallbackMessage
  }
}

/**
 * Typed error thrown by `apiRequest` for any non-2xx backend response.
 * `code` is the structured error code from the backend (e.g. 'SUBSCRIPTION_REQUIRED'),
 * which lets boundary components branch on the failure type instead of string-matching messages.
 */
export class ApiError extends Error {
  constructor(message: string, public status: number, public code?: string) {
    super(message)
    this.name = 'ApiError'
  }
}

export async function apiRequest<T>(
  endpoint: string,
  options?: RequestInit,
): Promise<T> {
  const response = await requestWithCredentials(endpoint, options)

  if (!response.ok) {
    const text = await response.text()
    let body: any = null
    try {
      body = text ? JSON.parse(text) : null
    } catch {
      // Body is not JSON — leave `body` null so we fall through to text/statusText.
    }

    const code: string | undefined = body?.error?.code
    const excerpt = text.replace(/\s+/g, ' ').trim().slice(0, 200)
    const fallback = excerpt || response.statusText || `API error ${response.status}`
    const message =
      response.status === 401
        ? 'Session expired'
        : body?.error?.message || body?.message || fallback

    throw new ApiError(message, response.status, code)
  }

  return response.json()
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DeviceResponse {
  id: string
  familyId: string
  isDeviceOwner?: boolean | null
  nanomdmDeviceId: string | null
  name: string | null
  platform: string
  osVersion: string | null
  lastSeen: string | null
  createdAt: string
  updatedAt: string
  isSupervised: boolean | null
  supervisedAt: string | null
  isEnrolled: boolean
  enrolledAt: string | null
  enrollmentStatus: string | null
  isConnected?: boolean
  policyCount?: number
  policyName?: string | null
  scheduleCount?: number
  scheduleName?: string | null
  primaryStudentId: string
  studentName: string | null
}

export interface DeviceWithNanoMDMData extends DeviceResponse {
  nanomdm?: {
    enrollmentId?: string
    deviceUdid?: string
    serialNumber?: string
    model?: string
    osVersion?: string
    lastSeenAt?: string
  }
  detectedDevice?: {
    model?: string
    osVersion?: string
  }
  hasActiveSchedule?: boolean
  activeScheduleName?: string | null
}

export interface DeviceListResponse {
  devices: DeviceResponse[]
  students: StudentResponse[]
  count: number
}

export interface CreateDeviceRequest {
  nanomdmDeviceId: string
  name?: string
  platform: 'ios' | 'android'
}

export interface UpdateDeviceRequest {
  name?: string
  primaryStudentId?: string
}

export interface DetectedDeviceResponse {
  id: string
  familyId: string
  udid: string
  name: string | null
  model: string | null
  osVersion: string | null
  isSupervised: boolean | null
  platform: string
  detectedAt: string
  createdAt: string
  updatedAt: string
}

export interface DetectedDeviceListResponse {
  devices: DetectedDeviceResponse[]
  count: number
}

export interface HelperStatusResponse {
  running: boolean
  platform: 'mac' | 'windows' | null
  signedInAs: string | null
  lastSeen: string | null
}

export type CommandType = 'supervise' | 'remove_supervision' | 'android_setup' | 'android_remove'
export type CommandStatus = 'pending' | 'in_progress' | 'completed' | 'failed'

export interface CommandResponse {
  id: string
  familyId: string
  type: CommandType
  udid: string | null
  deviceId: string | null
  status: CommandStatus
  createdAt: string
  startedAt: string | null
  completedAt: string | null
  error: string | null
  result: Record<string, unknown> | null
}

export interface SuperviseDeviceResponse {
  commandId: string
  status: CommandStatus
  device: DetectedDeviceResponse
}

export interface RemoveSupervisionResponse {
  commandId: string
  status: CommandStatus
  device: DeviceResponse
}

export interface SupervisionStatusResponse {
  isSupervised: boolean | null
  supervisedAt: string | null
  status: 'not_supervised' | 'supervised' | 'pending'
  commandId?: string | null
}

export interface DeviceStatusResponse {
  isSupervised: boolean | null
  supervisedAt: string | null
  isEnrolled: boolean
  enrolledAt: string | null
  enrollmentStatus: string | null
  nanomdmStatus: {
    connected: boolean
    lastSeen: string | null
    enrollmentId: string | null
  } | null
}

export interface EnrichedApp {
  id?: string
  bundleId: string
  packageName?: string
  name: string
  icon: string
}

export interface SyncStatus {
  status: 'ok' | 'partial'
  failedDeviceIds: string[]
}

export interface PolicyResponse {
  id: string
  name: string
  description: string | null
  config: Record<string, unknown>
  familyId?: string | null
  presetSlug?: string | null
  sortOrder?: number | null
  createdAt?: string
  updatedAt?: string
  apps?: EnrichedApp[]
  assignedDeviceCount?: number
}

export interface UpdatePolicyResponse extends PolicyResponse {
  sync: SyncStatus
}

export interface PolicyAssignmentResponse {
  id: string
  deviceId: string
  policyId: string
  status: 'pending' | 'applied' | 'failed' | 'removed'
  appliedAt: string | null
  createdAt: string
  updatedAt: string
  policy: PolicyResponse
}

export interface PolicyAssignmentListResponse {
  assignments: Array<{
    id: string
    policy: PolicyResponse & { familyId?: string | null; presetSlug?: string | null; schoolId?: string | null }
    status: 'pending' | 'applied' | 'failed' | 'removed'
    appliedAt: string | null
    createdAt: string
    updatedAt: string
    userChoiceOverrides?: Record<string, boolean> | null
    sourceType?: 'MANUAL' | 'SCHEDULE' | 'SCHOOL'
    sourceSchoolId?: string | null
  }>
  count: number
}

export interface PolicyListResponse {
  policies: PolicyResponse[]
  count: number
}

export interface CreatePolicyRequest {
  name: string
  description?: string
  config: Record<string, unknown>
}

export interface UpdatePolicyRequest {
  name?: string
  description?: string
  config?: Record<string, unknown>
}

export interface AppSearchResult {
  id?: string
  bundleId?: string
  packageName?: string
  name: string
  icon: string
  developer: string
  category: string
  source?: 'catalog' | 'itunes' | 'playstore'
  platform?: 'ios' | 'android' | 'both'
  price?: string
  version?: string
  releaseDate?: string
}

export interface AppSearchResponse {
  apps: AppSearchResult[]
  count: number
}

export interface DomainMappingResult {
  domain: string
  serviceName: string
  serviceId: string | null
  bundleId: string | null
  category: string | null
}

export interface UrlSuggestionsResponse {
  suggestions: DomainMappingResult[]
}

export type MilestoneStatus = 'pending' | 'in_progress' | 'completed' | 'failed'

export interface CommandMilestoneResponse {
  id: string
  commandId: string
  milestone: string
  status: MilestoneStatus
  message: string | null
  percentage: number | null
  createdAt: string
  completedAt: string | null
}

export interface CommandMilestoneListResponse {
  milestones: CommandMilestoneResponse[]
  count: number
}

export interface OnboardingStatusResponse {
  onboardingCompleted: boolean
  onboardingCompletedAt: string | null
}

export interface OnboardingPrerequisitesResponse {
  hasDevices: boolean
  hasPolicies: boolean
  hasSchedules: boolean
}

export interface ScheduleBlock {
  id: string
  dayOfWeek: number
  startTime: string
  endTime: string
}

export interface ScheduleDevice {
  id: string
  deviceId: string
  device: {
    id: string
    name: string | null
    platform: string
  }
}

export interface ScheduleResponse {
  id: string
  familyId: string
  name: string
  policyId: string
  timezone: string
  isEnabled: boolean
  createdAt: string
  updatedAt: string
  policy: {
    id: string
    name: string
  }
  blocks: ScheduleBlock[]
  devices: ScheduleDevice[]
  sync?: SyncStatus
}

export interface ScheduleListResponse {
  schedules: ScheduleResponse[]
  count: number
}

export interface CreateScheduleRequest {
  name: string
  policyId: string
  timezone: string
  blocks: Array<{ dayOfWeek: number; startTime: string; endTime: string }>
  deviceIds: string[]
}

export interface UpdateScheduleRequest {
  name?: string
  policyId?: string
  timezone?: string
  blocks?: Array<{ dayOfWeek: number; startTime: string; endTime: string }>
  deviceIds?: string[]
}

export interface ConnectionTokenResponse {
  token: string
  expiresAt: string
}

export type SubscriptionStatus = 'active' | 'past_due' | 'canceled' | 'none' | 'comped'

export interface AdminUser {
  id: string
  email: string
  name: string | null
  role: 'ADMIN' | 'USER'
  workosUserId: string | null
  familyId: string | null
  emailVerified: boolean
  subscriptionStatus: SubscriptionStatus
  subscriptionPlan: string | null
  stripeCustomerId: string | null
  stripeSubscriptionId: string | null
  currentPeriodEnd: string | null
  createdAt: string
  updatedAt: string
}

export interface AdminUserListResponse {
  users: AdminUser[]
  pagination: {
    hasMore: boolean
    before: string | null
    after: string | null
  }
}

export interface PromoCode {
  id: string
  code: string
  description: string | null
  expiresAt: string | null
  maxUses: number
  usedCount: number
  usedBy: string | null
  usedAt: string | null
  createdBy: string
  createdAt: string
  isRevoked: boolean
}

export interface PromoCodeListResponse {
  promoCodes: PromoCode[]
}

export interface PromoCodeValidationResponse {
  valid: boolean
  code?: string
  error?: string
}

export interface AdminSystemRule {
  id: string
  name: string
  description: string | null
  presetSlug: string | null
  sortOrder: number | null
  config: Record<string, unknown>
  assignmentCount: number
  createdAt: string
  updatedAt: string
}

export interface UserChoiceOption {
  id: string
  slug: string
  label: string
  bundleIds: string[]
  domains: string[]
  createdAt: string
  updatedAt: string
}

export interface PolicyRuntimeMetricsCounters {
  applyRequests: number
  applyNoopAlreadyApplied: number
  applyReinstalls: number
  applyRemoveBeforeInstallSent: number
  removeRequests: number
  removeDeferredSourcesRemain: number
  removeSkippedAlreadyRemovingOrRaceLost: number
  removeEnqueueStarted: number
  removeEnqueueSucceeded: number
  removeEnqueueFailed: number
  notNowReceived: number
  notNowRetryScheduled: number
  notNowRetrySkippedStale: number
  notNowRetryPushSent: number
  notNowRetryPushFailed: number
}

export interface PolicyRuntimeMetricsResponse {
  startedAt: string
  counters: PolicyRuntimeMetricsCounters
}

export interface AdminSchool {
  id: string
  name: string
  slug: string
  timezone: string | null
  seatLimit: number
  billingStatus: string | null
  billingPlan: string | null
  staffCount?: number
  seatUsage?: number
  createdAt: string
  updatedAt: string
}

export interface AdminSchoolStaff {
  id: string
  userId: string
  schoolId: string
  email: string
  name: string | null
  role: 'admin' | 'staff'
  status: 'active' | 'inactive'
  createdAt: string
}

export interface AdminAuditLog {
  id: string
  action: string
  userId: string | null
  userEmail?: string | null
  userName?: string | null
  familyId: string | null
  schoolId: string | null
  staffUserId: string | null
  resource: string | null
  details: Record<string, unknown> | null
  ipAddress: string | null
  userAgent: string | null
  createdAt: string
}

export interface AdminAuditLogResponse {
  logs: AdminAuditLog[]
  total: number
  hasMore: boolean
}

// ---------------------------------------------------------------------------
// API methods
// ---------------------------------------------------------------------------

export const api = {
  // Device endpoints
  getDevices: (): Promise<DeviceListResponse> =>
    apiRequest<DeviceListResponse>('/api/v1/devices'),

  getDevice: (id: string): Promise<DeviceWithNanoMDMData> =>
    apiRequest<DeviceWithNanoMDMData>(`/api/v1/devices/${id}`),

  createDevice: (data: CreateDeviceRequest): Promise<DeviceResponse> =>
    apiRequest<DeviceResponse>('/api/v1/devices', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateDevice: (id: string, data: UpdateDeviceRequest): Promise<DeviceResponse> =>
    apiRequest<DeviceResponse>(`/api/v1/devices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getDetectedDevices: (): Promise<DetectedDeviceListResponse> =>
    apiRequest<DetectedDeviceListResponse>('/api/v1/devices/detected'),

  getHelperStatus: (): Promise<HelperStatusResponse> =>
    apiRequest<HelperStatusResponse>('/api/v1/helper/status'),

  superviseDetectedDevice: (udid: string, primaryStudentId?: string): Promise<SuperviseDeviceResponse> =>
    apiRequest<SuperviseDeviceResponse>(`/api/v1/devices/detected/${udid}/supervise`, {
      method: 'POST',
      body: JSON.stringify(primaryStudentId ? { primaryStudentId } : {}),
    }),

  androidSetupDevice: (udid: string, primaryStudentId?: string): Promise<SuperviseDeviceResponse> =>
    apiRequest<SuperviseDeviceResponse>(`/api/v1/devices/detected/${udid}/android-setup`, {
      method: 'POST',
      body: JSON.stringify(primaryStudentId ? { primaryStudentId } : {}),
    }),

  emergencyUnlockAndroidDevice: (deviceId: string): Promise<{ success: boolean; message: string }> =>
    apiRequest<{ success: boolean; message: string }>(`/api/v1/android/devices/${deviceId}/emergency-unlock`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  removeSupervision: (deviceId: string): Promise<RemoveSupervisionResponse> =>
    apiRequest<RemoveSupervisionResponse>(`/api/v1/devices/${deviceId}/remove-supervision`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  androidRemoveDevice: (deviceId: string): Promise<RemoveSupervisionResponse> =>
    apiRequest<RemoveSupervisionResponse>(`/api/v1/android/devices/${deviceId}/remove`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  superviseDevice: (deviceId: string): Promise<SuperviseDeviceResponse> =>
    apiRequest<SuperviseDeviceResponse>(`/api/v1/devices/${deviceId}/supervise`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  getCommandStatus: (commandId: string): Promise<CommandResponse> =>
    apiRequest<CommandResponse>(`/api/v1/commands/${commandId}`),

  getCommandMilestones: (commandId: string): Promise<CommandMilestoneListResponse> =>
    apiRequest<CommandMilestoneListResponse>(`/api/v1/commands/${commandId}/milestones`),

  getActiveCommands: (): Promise<{ commands: CommandResponse[]; count: number }> =>
    apiRequest<{ commands: CommandResponse[]; count: number }>('/api/v1/commands/active'),

  getSupervisionStatus: (deviceId: string): Promise<SupervisionStatusResponse> =>
    apiRequest<SupervisionStatusResponse>(`/api/v1/devices/${deviceId}/supervision-status`),

  getDeviceStatus: (deviceId: string): Promise<DeviceStatusResponse> =>
    apiRequest<DeviceStatusResponse>(`/api/v1/devices/${deviceId}/status`),

  // Policy endpoints
  applyPolicy: (deviceId: string, policyId: string): Promise<PolicyAssignmentResponse> =>
    apiRequest<PolicyAssignmentResponse>(`/api/v1/devices/${deviceId}/policies`, {
      method: 'POST',
      body: JSON.stringify({ policyId }),
    }),

  removePolicy: (deviceId: string, policyId: string): Promise<{ success: boolean; message: string }> =>
    apiRequest<{ success: boolean; message: string }>(`/api/v1/devices/${deviceId}/policies/${policyId}`, {
      method: 'DELETE',
    }),

  getDevicePolicies: (deviceId: string): Promise<PolicyAssignmentListResponse> =>
    apiRequest<PolicyAssignmentListResponse>(`/api/v1/devices/${deviceId}/policies`),

  updateDevicePolicyUserChoiceOverrides: (
    deviceId: string,
    policyId: string,
    userChoiceOverrides: Record<string, boolean>,
  ): Promise<{ id: string; userChoiceOverrides: Record<string, boolean> | null }> =>
    apiRequest(`/api/v1/devices/${deviceId}/policies/${policyId}`, {
      method: 'PATCH',
      body: JSON.stringify({ userChoiceOverrides }),
    }),

  getPresetPolicy: (): Promise<PolicyResponse> =>
    apiRequest<PolicyResponse>('/api/v1/policies/preset'),

  getPolicies: (): Promise<PolicyListResponse> =>
    apiRequest<PolicyListResponse>('/api/v1/policies'),

  getPolicy: (id: string): Promise<PolicyResponse> =>
    apiRequest<PolicyResponse>(`/api/v1/policies/${id}`),

  createPolicy: (data: CreatePolicyRequest): Promise<PolicyResponse> =>
    apiRequest<PolicyResponse>('/api/v1/policies', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updatePolicy: (id: string, data: UpdatePolicyRequest): Promise<UpdatePolicyResponse> =>
    apiRequest<UpdatePolicyResponse>(`/api/v1/policies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deletePolicy: (id: string): Promise<{ success: boolean; message: string }> =>
    apiRequest<{ success: boolean; message: string }>(`/api/v1/policies/${id}`, {
      method: 'DELETE',
    }),

  getProtectedBundleIds: (): Promise<{ bundleIds: string[] }> =>
    apiRequest<{ bundleIds: string[] }>('/api/v1/policies/protected-bundle-ids'),

  getProtectedApps: (): Promise<{ apps: { bundleId: string | null; packageName: string | null; name: string; icon?: string; platform: string }[] }> =>
    apiRequest<{ apps: { bundleId: string | null; packageName: string | null; name: string; icon?: string; platform: string }[] }>('/api/v1/policies/protected-apps'),

  // App search endpoints
  searchApps: (query: string, limit?: number): Promise<AppSearchResponse> =>
    apiRequest<AppSearchResponse>(`/api/v1/apps/search?q=${encodeURIComponent(query)}${limit ? `&limit=${limit}` : ''}`),

  getAppsByBundleIds: (bundleIds: string[]): Promise<{ apps: { id?: string; bundleId?: string; packageName?: string; name: string; icon?: string }[] }> =>
    apiRequest(`/api/v1/apps/by-bundle-ids?bundleIds=${encodeURIComponent(bundleIds.join(','))}`),

  getAppSuggest: (query: string, limit?: number): Promise<{ apps: AppSearchResult[] }> =>
    apiRequest(`/api/v1/apps/suggest?q=${encodeURIComponent(query)}${limit != null ? `&limit=${limit}` : ''}`),

  requestApp: (data: { name: string; bundleId?: string; packageName?: string; icon?: string; source: string }): Promise<{ id: string; status: string }> =>
    apiRequest('/api/v1/apps/requests', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // URL suggestions
  getUrlSuggestions: (query: string): Promise<UrlSuggestionsResponse> =>
    apiRequest<UrlSuggestionsResponse>(`/api/v1/policies/url-suggestions?q=${encodeURIComponent(query)}`),

  getUrlsForBundleIds: (bundleIds: string[]): Promise<UrlSuggestionsResponse> =>
    apiRequest<UrlSuggestionsResponse>(`/api/v1/policies/url-suggestions?bundleIds=${encodeURIComponent(bundleIds.join(','))}`),

  getUrlsForService: (serviceName: string): Promise<UrlSuggestionsResponse> =>
    apiRequest<UrlSuggestionsResponse>(`/api/v1/policies/url-suggestions?exactService=${encodeURIComponent(serviceName)}`),

  // Onboarding
  getOnboardingStatus: (): Promise<OnboardingStatusResponse> =>
    apiRequest<OnboardingStatusResponse>('/api/v1/user/onboarding-status'),

  getOnboardingPrerequisites: (): Promise<OnboardingPrerequisitesResponse> =>
    apiRequest<OnboardingPrerequisitesResponse>('/api/v1/user/onboarding-prerequisites'),

  completeOnboarding: (): Promise<{ success: boolean; onboardingCompletedAt: string }> =>
    apiRequest('/api/v1/user/complete-onboarding', { method: 'POST' }),

  resetOnboarding: (): Promise<{ success: boolean }> =>
    apiRequest('/api/v1/user/reset-onboarding', { method: 'POST' }),

  // Schedule endpoints
  getSchedules: (): Promise<ScheduleListResponse> =>
    apiRequest<ScheduleListResponse>('/api/v1/schedules'),

  getSchedule: (id: string): Promise<ScheduleResponse> =>
    apiRequest<ScheduleResponse>(`/api/v1/schedules/${id}`),

  createSchedule: (data: CreateScheduleRequest): Promise<ScheduleResponse> =>
    apiRequest<ScheduleResponse>('/api/v1/schedules', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateSchedule: (id: string, data: UpdateScheduleRequest): Promise<ScheduleResponse> =>
    apiRequest<ScheduleResponse>(`/api/v1/schedules/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteSchedule: (id: string): Promise<{ success: boolean; message: string }> =>
    apiRequest<{ success: boolean; message: string }>(`/api/v1/schedules/${id}`, {
      method: 'DELETE',
    }),

  toggleSchedule: (id: string): Promise<{ id: string; isEnabled: boolean }> =>
    apiRequest<{ id: string; isEnabled: boolean }>(`/api/v1/schedules/${id}/toggle`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  // Admin endpoints
  getAdminUsers: (params?: { limit?: number; after?: string; before?: string; email?: string }): Promise<AdminUserListResponse> => {
    const queryParams = new URLSearchParams()
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.after) queryParams.append('after', params.after)
    if (params?.before) queryParams.append('before', params.before)
    if (params?.email) queryParams.append('email', params.email)
    return apiRequest<AdminUserListResponse>(`/api/v1/admin/users?${queryParams.toString()}`)
  },

  createAdminUser: (data: { email: string; password: string; name?: string; role?: 'ADMIN' | 'USER' }): Promise<{ user: AdminUser }> =>
    apiRequest('/api/v1/admin/users', { method: 'POST', body: JSON.stringify(data) }),

  updateAdminUser: (id: string, data: { email?: string; name?: string; role?: 'ADMIN' | 'USER'; password?: string }): Promise<{ user: AdminUser }> =>
    apiRequest(`/api/v1/admin/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteAdminUser: (id: string): Promise<{ message: string }> =>
    apiRequest(`/api/v1/admin/users/${id}`, { method: 'DELETE' }),

  resetUserPassword: (id: string, data: { password: string }): Promise<{ message: string }> =>
    apiRequest(`/api/v1/admin/users/${id}/reset-password`, { method: 'POST', body: JSON.stringify(data) }),

  updateUserSubscription: (id: string, data: { subscriptionStatus: SubscriptionStatus }): Promise<{ user: { id: string; email: string; subscriptionStatus: string; subscriptionPlan: string | null } }> =>
    apiRequest(`/api/v1/admin/users/${id}/subscription`, { method: 'PUT', body: JSON.stringify(data) }),

  // Admin promo codes
  getPromoCodes: (params?: { includeUsed?: boolean; includeRevoked?: boolean }): Promise<PromoCodeListResponse> => {
    const queryParams = new URLSearchParams()
    if (params?.includeUsed !== undefined) queryParams.append('includeUsed', params.includeUsed.toString())
    if (params?.includeRevoked !== undefined) queryParams.append('includeRevoked', params.includeRevoked.toString())
    const qs = queryParams.toString()
    return apiRequest(`/api/v1/admin/promo-codes${qs ? `?${qs}` : ''}`)
  },

  createPromoCode: (data: { code?: string; description?: string; expiresAt?: string }): Promise<{ promoCode: PromoCode }> =>
    apiRequest('/api/v1/admin/promo-codes', { method: 'POST', body: JSON.stringify(data) }),

  revokePromoCode: (id: string): Promise<{ message: string }> =>
    apiRequest(`/api/v1/admin/promo-codes/${id}`, { method: 'DELETE' }),

  // Admin system rules
  getAdminSystemRules: (): Promise<{ systemRules: AdminSystemRule[] }> =>
    apiRequest('/api/v1/admin/system-rules'),

  getAdminSystemRule: (id: string): Promise<AdminSystemRule> =>
    apiRequest(`/api/v1/admin/system-rules/${id}`),

  updateAdminSystemRule: (id: string, data: { name?: string; description?: string | null; sortOrder?: number | null; userChoiceOptions?: string[]; config?: Record<string, unknown> }): Promise<AdminSystemRule> =>
    apiRequest(`/api/v1/admin/system-rules/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  createAdminSystemRule: (data: { presetSlug: string; name: string; description?: string | null; sortOrder?: number | null; cloneFromId?: string; config?: Record<string, unknown> }): Promise<AdminSystemRule> =>
    apiRequest('/api/v1/admin/system-rules', { method: 'POST', body: JSON.stringify(data) }),

  deleteAdminSystemRule: (id: string): Promise<{ message: string }> =>
    apiRequest(`/api/v1/admin/system-rules/${id}`, { method: 'DELETE' }),

  // Admin user-choice options
  getUserChoiceOptions: (): Promise<{ options: UserChoiceOption[] }> =>
    apiRequest('/api/v1/admin/user-choice-options'),

  createUserChoiceOption: (data: { slug: string; label: string; bundleIds: string[]; domains: string[] }): Promise<UserChoiceOption> =>
    apiRequest('/api/v1/admin/user-choice-options', { method: 'POST', body: JSON.stringify(data) }),

  updateUserChoiceOption: (id: string, data: { label?: string; bundleIds?: string[]; domains?: string[] }): Promise<UserChoiceOption> =>
    apiRequest(`/api/v1/admin/user-choice-options/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  deleteUserChoiceOption: (id: string): Promise<{ message: string }> =>
    apiRequest(`/api/v1/admin/user-choice-options/${id}`, { method: 'DELETE' }),

  // Admin policy runtime metrics
  getAdminPolicyRuntimeMetrics: (): Promise<PolicyRuntimeMetricsResponse> =>
    apiRequest('/api/v1/admin/runtime/policy-metrics'),

  // Admin protected apps (read-only — managed via curated catalog import)
  getAdminProtectedApps: (): Promise<{ bundleIds: string[]; items: { bundleId: string | null; packageName: string | null; name: string; icon?: string; platform: string }[] }> =>
    apiRequest('/api/v1/admin/protected-apps'),

  // Admin schools
  getAdminSchools: (): Promise<{ schools: AdminSchool[] }> =>
    apiRequest('/api/v1/admin/schools'),

  createAdminSchool: (data: { name: string; slug: string; timezone?: string; seatLimit?: number }): Promise<{ school: AdminSchool }> =>
    apiRequest('/api/v1/admin/schools', { method: 'POST', body: JSON.stringify(data) }),

  updateAdminSchool: (id: string, data: { name?: string; slug?: string; timezone?: string; seatLimit?: number }): Promise<{ school: AdminSchool }> =>
    apiRequest(`/api/v1/admin/schools/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  deleteAdminSchool: (id: string): Promise<{ message: string }> =>
    apiRequest(`/api/v1/admin/schools/${id}`, { method: 'DELETE' }),

  getAdminSchoolStaff: (schoolId: string): Promise<{ staff: AdminSchoolStaff[] }> =>
    apiRequest(`/api/v1/admin/schools/${schoolId}/staff`),

  addAdminSchoolStaff: (schoolId: string, data: { email: string; role: 'admin' | 'staff'; name?: string }): Promise<{ staff: AdminSchoolStaff; invited: boolean }> =>
    apiRequest(`/api/v1/admin/schools/${schoolId}/staff`, { method: 'POST', body: JSON.stringify(data) }),

  removeAdminSchoolStaff: (schoolId: string, staffId: string): Promise<{ message: string }> =>
    apiRequest(`/api/v1/admin/schools/${schoolId}/staff/${staffId}`, { method: 'DELETE' }),

  // Admin audit logs
  getAdminAuditLogs: (params?: { userId?: string; action?: string; resource?: string; startDate?: string; endDate?: string; schoolId?: string; limit?: number; offset?: number }): Promise<AdminAuditLogResponse> => {
    const queryParams = new URLSearchParams()
    if (params?.userId) queryParams.append('userId', params.userId)
    if (params?.action) queryParams.append('action', params.action)
    if (params?.resource) queryParams.append('resource', params.resource)
    if (params?.startDate) queryParams.append('startDate', params.startDate)
    if (params?.endDate) queryParams.append('endDate', params.endDate)
    if (params?.schoolId) queryParams.append('schoolId', params.schoolId)
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.offset) queryParams.append('offset', params.offset.toString())
    const qs = queryParams.toString()
    return apiRequest(`/api/v1/admin/audit-logs${qs ? `?${qs}` : ''}`)
  },

  // Promo code validation (public)
  validatePromoCode: (code: string): Promise<PromoCodeValidationResponse> =>
    apiRequest<PromoCodeValidationResponse>('/api/v1/billing/validate-promo', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
}

// Helper app info (public, no auth)
export interface HelperAppPlatformInfo {
  version: string
  minOS: string
}

export interface HelperAppInfo {
  mac: HelperAppPlatformInfo | null
  windows: HelperAppPlatformInfo | null
}

export async function fetchHelperAppInfo(): Promise<HelperAppInfo> {
  const res = await fetch(`${API_URL}/api/v1/helper-app/info`)
  if (!res.ok) return { mac: null, windows: null }
  return res.json()
}

// Connection token API
export const connectionTokenApi = {
  generateConnectionToken: (): Promise<ConnectionTokenResponse> =>
    apiRequest<ConnectionTokenResponse>('/api/v1/auth/connection-token', {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  connectWithToken: (token: string): Promise<{ sessionToken: string; user: { id: string; email: string; name: string | null; familyId: string | null } }> =>
    apiRequest('/api/v1/auth/connect', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
}

// School API
export interface SchoolRosterEntry {
  id: string
  familyId: string
  familyName: string
  userName: string | null
  userEmail: string
  managed: boolean
  status: 'active' | 'invited' | 'suspended'
  deviceCount: number
  groups: { id: string; name: string }[]
  invitedAt: string | null
  joinedAt: string | null
  createdAt: string
}

export interface SchoolRosterResponse {
  roster: SchoolRosterEntry[]
  total: number
  limit: number
  offset: number
}

export interface SchoolRosterFilters {
  status?: 'active' | 'invited' | 'suspended'
  groupId?: string
  search?: string
  limit?: number
  offset?: number
}

export interface SchoolGroup {
  id: string
  name: string
  description: string | null
  createdAt: string
  updatedAt: string
}

export interface SchoolPolicy {
  id: string
  name: string
  description: string | null
  config: Record<string, unknown>
  groupId: string | null
  assignedDeviceCount: number
  createdAt: string
  updatedAt: string
}

export interface SchoolSchedule {
  id: string
  name: string
  policyId: string
  timezone: string
  isEnabled: boolean
  policy: { id: string; name: string }
  blocks: { dayOfWeek: number; startTime: string; endTime: string }[]
  createdAt: string
  updatedAt: string
}

export interface SchoolStaffMember {
  id: string
  userId: string
  email: string
  name: string | null
  role: 'admin' | 'staff'
  createdAt: string
}

export interface SchoolGroupMember {
  groupMembershipId: string
  membershipId: string
  familyName: string
  status: 'active' | 'invited' | 'suspended'
}

export type SchoolStaffRole = 'admin' | 'staff'

export interface SchoolImportRow {
  name: string
  email?: string
}

export interface SchoolImportError {
  row: number
  column: string
  message: string
}

export interface SchoolImportResult {
  dryRun: boolean
  totalRows: number
  created: { row: number; name: string }[]
  skipped: { row: number; reason: string }[]
  errors: SchoolImportError[]
}

// ── School helper / enrollment types ──

export interface SchoolDetectedDevice {
  id: string
  schoolId: string | null
  udid: string
  name: string | null
  model: string | null
  osVersion: string | null
  isSupervised: boolean | null
  detectedAt: string
  createdAt: string
  updatedAt: string
  platform?: string  // 'ios' | 'android'
}

export interface SchoolDetectedDevicesResponse {
  devices: SchoolDetectedDevice[]
  count: number
}

export interface SchoolHelperStudent {
  id: string
  familyId: string
  familyName: string
  userName: string | null
  userEmail: string
  deviceCount: number
  groups: { id: string; name: string }[]
}

export interface SchoolHelperRosterResponse {
  students: SchoolHelperStudent[]
  total: number
  limit: number
  offset: number
}

export interface SchoolHelperCommand {
  id: string
  type: string
  udid: string | null
  deviceId: string | null
  status: string
  requestedByUserId: string | null
  deviceName: string | null
  studentName: string | null
  createdAt: string
  startedAt: string | null
  completedAt: string | null
  error: string | null
}

export interface SchoolHelperCommandsResponse {
  commands: SchoolHelperCommand[]
  total: number
  limit: number
  offset: number
}

export interface SchoolSuperviseResponse {
  commandId: string
  status: string
  familyId: string
  schoolMembershipId: string
  device: {
    id: string
    udid: string
    name: string | null
    model: string | null
    osVersion: string | null
    isSupervised: boolean | null
    detectedAt: string
  }
}

// ──────────────── Students ────────────────

export interface StudentResponse {
  id: string
  familyId: string
  name: string
  birthDate: string | null
  status: string
  source: string
  createdAt: string
  updatedAt: string
}

export interface StudentDetailResponse extends StudentResponse {
  deviceCount: number
  enrollmentCount: number
}

export const studentApi = {
  list: (): Promise<{ students: StudentResponse[]; count: number }> =>
    apiRequest('/api/v1/students'),

  get: (id: string): Promise<{ student: StudentDetailResponse }> =>
    apiRequest(`/api/v1/students/${id}`),

  create: (data: { name: string; birthDate?: string | null }): Promise<{ student: StudentResponse }> =>
    apiRequest('/api/v1/students', { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: { name?: string; birthDate?: string | null; archive?: boolean }): Promise<{ student: StudentResponse }> =>
    apiRequest(`/api/v1/students/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
}

export const schoolApi = {
  getSseToken: async (schoolId: string): Promise<{ token: string; expiresIn: number } | null> => {
    try {
      const response = await requestWithCredentials('/api/v1/auth/sse-token', {
        method: 'POST',
        body: JSON.stringify({ schoolId }),
      })
      if (!response.ok) return null
      return response.json()
    } catch {
      return null
    }
  },
  getRoster: (schoolId: string, filters?: SchoolRosterFilters): Promise<SchoolRosterResponse> => {
    const params = new URLSearchParams()
    if (filters?.status) params.append('status', filters.status)
    if (filters?.groupId) params.append('groupId', filters.groupId)
    if (filters?.search) params.append('search', filters.search)
    if (filters?.limit) params.append('limit', filters.limit.toString())
    if (filters?.offset) params.append('offset', filters.offset.toString())
    const qs = params.toString()
    return apiRequest(`/api/v1/school/${schoolId}/roster${qs ? `?${qs}` : ''}`)
  },

  getGroups: (schoolId: string): Promise<{ groups: SchoolGroup[] }> =>
    apiRequest(`/api/v1/school/${schoolId}/groups`),

  createGroup: (schoolId: string, data: { name: string; description?: string }): Promise<SchoolGroup> =>
    apiRequest(`/api/v1/school/${schoolId}/groups`, { method: 'POST', body: JSON.stringify(data) }),

  updateGroup: (schoolId: string, groupId: string, data: { name?: string; description?: string }): Promise<SchoolGroup> =>
    apiRequest(`/api/v1/school/${schoolId}/groups/${groupId}`, { method: 'PATCH', body: JSON.stringify(data) }),

  deleteGroup: (schoolId: string, groupId: string): Promise<{ success: boolean }> =>
    apiRequest(`/api/v1/school/${schoolId}/groups/${groupId}`, { method: 'DELETE' }),

  getGroupMembers: (schoolId: string, groupId: string): Promise<{ members: SchoolGroupMember[] }> =>
    apiRequest(`/api/v1/school/${schoolId}/groups/${groupId}/members`),

  addGroupMember: (schoolId: string, groupId: string, membershipId: string): Promise<{ groupMembershipId: string }> =>
    apiRequest(`/api/v1/school/${schoolId}/groups/${groupId}/members`, { method: 'POST', body: JSON.stringify({ membershipId }) }),

  removeGroupMember: (schoolId: string, groupId: string, membershipId: string): Promise<{ message: string }> =>
    apiRequest(`/api/v1/school/${schoolId}/groups/${groupId}/members/${membershipId}`, { method: 'DELETE' }),

  getPolicies: (schoolId: string): Promise<{ policies: SchoolPolicy[] }> =>
    apiRequest(`/api/v1/school/${schoolId}/policies`),

  getPolicy: (schoolId: string, policyId: string): Promise<SchoolPolicy> =>
    apiRequest(`/api/v1/school/${schoolId}/policies/${policyId}`).then((r) => (r as { policy: SchoolPolicy }).policy),

  createPolicy: (schoolId: string, data: { name: string; description?: string; config: Record<string, unknown>; groupId?: string }): Promise<SchoolPolicy> =>
    apiRequest(`/api/v1/school/${schoolId}/policies`, { method: 'POST', body: JSON.stringify(data) }).then((r) => (r as { policy: SchoolPolicy }).policy),

  updatePolicy: (schoolId: string, policyId: string, data: { name?: string; description?: string; config?: Record<string, unknown>; groupId?: string | null }): Promise<SchoolPolicy> =>
    apiRequest(`/api/v1/school/${schoolId}/policies/${policyId}`, { method: 'PATCH', body: JSON.stringify(data) }).then((r) => (r as { policy: SchoolPolicy }).policy),

  deletePolicy: (schoolId: string, policyId: string): Promise<{ message: string }> =>
    apiRequest(`/api/v1/school/${schoolId}/policies/${policyId}`, { method: 'DELETE' }),

  getSchedules: (schoolId: string): Promise<{ schedules: SchoolSchedule[] }> =>
    apiRequest(`/api/v1/school/${schoolId}/schedules`),

  createSchedule: (schoolId: string, data: { name: string; policyId: string; timezone: string; blocks: { dayOfWeek: number; startTime: string; endTime: string }[] }): Promise<SchoolSchedule> =>
    apiRequest(`/api/v1/school/${schoolId}/schedules`, { method: 'POST', body: JSON.stringify(data) }),

  updateSchedule: (schoolId: string, scheduleId: string, data: { name?: string; policyId?: string; timezone?: string; blocks?: { dayOfWeek: number; startTime: string; endTime: string }[] }): Promise<SchoolSchedule> =>
    apiRequest(`/api/v1/school/${schoolId}/schedules/${scheduleId}`, { method: 'PATCH', body: JSON.stringify(data) }),

  deleteSchedule: (schoolId: string, scheduleId: string): Promise<{ success: boolean }> =>
    apiRequest(`/api/v1/school/${schoolId}/schedules/${scheduleId}`, { method: 'DELETE' }),

  toggleSchedule: (schoolId: string, scheduleId: string): Promise<{ id: string; isEnabled: boolean }> =>
    apiRequest(`/api/v1/school/${schoolId}/schedules/${scheduleId}/toggle`, { method: 'POST', body: JSON.stringify({}) }),

  getStaff: (schoolId: string): Promise<{ staff: SchoolStaffMember[] }> =>
    apiRequest(`/api/v1/school/${schoolId}/staff`),

  inviteStaff: (schoolId: string, data: { email: string; role: 'admin' | 'staff' }): Promise<SchoolStaffMember> =>
    apiRequest(`/api/v1/school/${schoolId}/staff`, { method: 'POST', body: JSON.stringify(data) }),

  updateStaffRole: (schoolId: string, staffId: string, role: 'admin' | 'staff'): Promise<SchoolStaffMember> =>
    apiRequest(`/api/v1/school/${schoolId}/staff/${staffId}`, { method: 'PATCH', body: JSON.stringify({ role }) }),

  removeStaff: (schoolId: string, staffId: string): Promise<{ success: boolean }> =>
    apiRequest(`/api/v1/school/${schoolId}/staff/${staffId}`, { method: 'DELETE' }),

  getSchool: (schoolId: string): Promise<{ id: string; name: string; createdAt: string }> =>
    apiRequest(`/api/v1/school/${schoolId}`),

  updateSchool: (schoolId: string, data: { name?: string }): Promise<{ id: string; name: string }> =>
    apiRequest(`/api/v1/school/${schoolId}`, { method: 'PATCH', body: JSON.stringify(data) }),

  getBilling: (schoolId: string): Promise<{ billing: { billingStatus: string; billingPlan: string | null; currentPeriodEnd: string | null; seatLimit: number; seatsUsed: number; seatsAvailable: number } }> =>
    apiRequest(`/api/v1/school/${schoolId}/billing`),

  getSeats: (schoolId: string): Promise<{ seats: { seatLimit: number; active: number; invited: number; suspended: number; total: number; available: number } }> =>
    apiRequest(`/api/v1/school/${schoolId}/billing/seats`),

  importRoster: (schoolId: string, data: { dryRun: boolean; rows: SchoolImportRow[] }): Promise<SchoolImportResult> =>
    apiRequest(`/api/v1/school/${schoolId}/roster/import`, { method: 'POST', body: JSON.stringify(data) }),

  createManagedStudent: (schoolId: string, data: { name: string; email?: string; groupIds?: string[] }): Promise<{ student: SchoolRosterEntry; message?: string }> =>
    apiRequest(`/api/v1/school/${schoolId}/roster/managed`, { method: 'POST', body: JSON.stringify(data) }),

  deleteManagedStudent: (schoolId: string, membershipId: string): Promise<{ success: boolean }> =>
    apiRequest(`/api/v1/school/${schoolId}/roster/${membershipId}`, { method: 'DELETE' }),

  // ── Helper / Enrollment endpoints ──

  getDetectedDevices: (schoolId: string): Promise<SchoolDetectedDevicesResponse> =>
    apiRequest(`/api/v1/school/${schoolId}/helper/detected`),

  getHelperRoster: (schoolId: string, filters?: { search?: string; groupId?: string; limit?: number; offset?: number }): Promise<SchoolHelperRosterResponse> => {
    const params = new URLSearchParams()
    if (filters?.search) params.append('search', filters.search)
    if (filters?.groupId) params.append('groupId', filters.groupId)
    if (filters?.limit) params.append('limit', filters.limit.toString())
    if (filters?.offset) params.append('offset', filters.offset.toString())
    const qs = params.toString()
    return apiRequest(`/api/v1/school/${schoolId}/helper/roster${qs ? `?${qs}` : ''}`)
  },

  getHelperCommands: (schoolId: string, status?: string): Promise<SchoolHelperCommandsResponse> => {
    const params = new URLSearchParams()
    if (status) params.append('status', status)
    return apiRequest(`/api/v1/school/${schoolId}/helper/commands${params.toString() ? `?${params.toString()}` : ''}`)
  },

  superviseDetectedDevice: (schoolId: string, udid: string, schoolMembershipId: string): Promise<SchoolSuperviseResponse> =>
    apiRequest(`/api/v1/school/${schoolId}/helper/detected/${encodeURIComponent(udid)}/supervise`, {
      method: 'POST',
      body: JSON.stringify({ schoolMembershipId }),
    }),

  androidSetupDevice: (schoolId: string, udid: string, schoolMembershipId: string): Promise<SchoolSuperviseResponse> =>
    apiRequest(`/api/v1/school/${schoolId}/helper/detected/${encodeURIComponent(udid)}/android-setup`, {
      method: 'POST',
      body: JSON.stringify({ schoolMembershipId }),
    }),

  removeSupervision: (schoolId: string, deviceId: string): Promise<{ commandId: string; status: string; device: { id: string; nanomdmDeviceId: string | null; name: string | null } }> =>
    apiRequest(`/api/v1/school/${schoolId}/devices/${deviceId}/remove-supervision`, {
      method: 'POST',
    }),

  androidRemoveDevice: (schoolId: string, deviceId: string): Promise<{ commandId: string; status: string; device: { id: string; nanomdmDeviceId: string | null; name: string | null } }> =>
    apiRequest(`/api/v1/school/${schoolId}/devices/${deviceId}/android-remove`, {
      method: 'POST',
    }),

  getMembershipDevices: (schoolId: string, membershipId: string): Promise<{ devices: Array<{ id: string; nanomdmDeviceId: string | null; name: string | null; platform: string; isEnrolled: boolean; isSupervised: boolean | null; isConnected: boolean; lastSeen: string | null; createdAt: string }> }> =>
    apiRequest(`/api/v1/school/${schoolId}/roster/${membershipId}/devices`),

  getCommandStatus: (schoolId: string, commandId: string): Promise<CommandResponse> =>
    apiRequest<CommandResponse>(`/api/v1/school/${schoolId}/commands/${commandId}`),

  getCommandMilestones: (schoolId: string, commandId: string): Promise<CommandMilestoneListResponse> =>
    apiRequest<CommandMilestoneListResponse>(`/api/v1/school/${schoolId}/commands/${commandId}/milestones`),
}
